import { AxiosInstance } from 'axios';

import {
  AllocationGroupAPIApiFactory,
  WorkloadReportAPIApiFactory,
  ClusterReportAPIApiFactory,
  OrganizationOverviewAPIApiFactory,
  ReportConfigurationAPIApiFactory,
  NamespaceReportAPIApiFactory,
  PlatformImpactReportAPIApiFactory,
  NodeReportAPIApiFactory,
} from '@cast/openapi';
import {
  ValueRealizationAPIApiFactory,
  ClusterScoreAPIApiFactory,
  ContainerLiveMigrationAPIApiFactory,
  NodeTemplateAPIApiFactory,
  ReliabilityMetricsAPIApiFactory,
  BudgetsAPIApiFactory,
} from '@cast/openapi-reporting';
import type {
  CustomDashboardsQueryResponse,
  CustomDashboardsSchemaResponse,
  CubeQuery,
} from '@cast/types';

import { AXIOS_CONFIGURATION } from '../constants';

// prettier-ignore
export const CostReportApiAdapter = (client: AxiosInstance) => {
  const generatedClusterReportApi = ClusterReportAPIApiFactory(AXIOS_CONFIGURATION, '', client);
  const organizationOverviewApi = OrganizationOverviewAPIApiFactory(AXIOS_CONFIGURATION, '', client);
  const generatedAllocationGroupsApi = AllocationGroupAPIApiFactory(AXIOS_CONFIGURATION, '', client);
  const generatedWorkloadsApi = WorkloadReportAPIApiFactory(AXIOS_CONFIGURATION, '', client);
  const generatedNamespaceApi = NamespaceReportAPIApiFactory(AXIOS_CONFIGURATION, '', client);
  const generatedReportConfigurationApi = ReportConfigurationAPIApiFactory(AXIOS_CONFIGURATION, '', client);
  const platformImpactReportApi = PlatformImpactReportAPIApiFactory(AXIOS_CONFIGURATION, '', client);
  const nodeReportApi = NodeReportAPIApiFactory(AXIOS_CONFIGURATION, '', client);
  const valueReportApi = ValueRealizationAPIApiFactory(AXIOS_CONFIGURATION, '', client);
  const clusterScoreApi = ClusterScoreAPIApiFactory(AXIOS_CONFIGURATION, '', client);
  const nodeTemplateApi = NodeTemplateAPIApiFactory(
    AXIOS_CONFIGURATION,
    '',
    client
  );
  const budgetsApi = BudgetsAPIApiFactory(AXIOS_CONFIGURATION, '', client);
  const containerLiveMigrationApi = ContainerLiveMigrationAPIApiFactory(
    AXIOS_CONFIGURATION,
    '',
    client
  );
  const reliabilityMetricsApi = ReliabilityMetricsAPIApiFactory(
    AXIOS_CONFIGURATION,
    '',
    client
  );

  return {
    getAllocationGroups: generatedAllocationGroupsApi.allocationGroupAPIListAllocationGroups,
    createAllocationGroup: generatedAllocationGroupsApi.allocationGroupAPICreateAllocationGroup,
    updateAllocationGroup: generatedAllocationGroupsApi.allocationGroupAPIUpdateAllocationGroup,
    deleteAllocationGroup: generatedAllocationGroupsApi.allocationGroupAPIDeleteAllocationGroup,
    getAllocationGroupSummaries: generatedAllocationGroupsApi.allocationGroupAPIGetAllocationGroupCostSummaries,
    getAllocationGroupTimedSummaries: generatedAllocationGroupsApi.allocationGroupAPIGetAllocationGroupCostTimedSummaries,
    getAllocationGroupTotalCosts: generatedAllocationGroupsApi.allocationGroupAPIGetAllocationGroupTotalCostTimed,
    getAllocationGroupDataTransferSummary: generatedAllocationGroupsApi.allocationGroupAPIGetCostAllocationGroupDataTransferSummary,
    getAllocationGroupWorkloads: generatedAllocationGroupsApi.allocationGroupAPIGetAllocationGroupWorkloadCosts,
    getAllocationGroupDataTransferWorkloads: generatedAllocationGroupsApi.allocationGroupAPIGetCostAllocationGroupDataTransferWorkloads,
    getAllocationGroupEfficiencySummary:generatedAllocationGroupsApi.allocationGroupAPIGetAllocationGroupEfficiencySummary,
    getAllocationGroupWorkloadsEfficiency:generatedAllocationGroupsApi.allocationGroupAPIGetAllocationGroupWorkloadsEfficiency,

    getSingleWorkloadCostReport: generatedWorkloadsApi.workloadReportAPIGetSingleWorkloadCostReport,
    getWorkloadReliabilitySummaries:
      generatedWorkloadsApi.workloadReportAPIGetWorkloadReliabilitySummaries,
    getSingleWorkloadReliabilityMetrics:
      generatedWorkloadsApi.workloadReportAPIGetSingleWorkloadReliabilityMetrics,
    getWorkloadReliabilityMetrics:
      generatedWorkloadsApi.workloadReportAPIGetWorkloadReliabilityMetrics,
    getWorkloadLabelNames: generatedWorkloadsApi.workloadReportAPIGetWorkloadLabelNames,
    getNamespaces: generatedWorkloadsApi.workloadReportAPIGetNamespacesForClusters,
    getWorkloadLabelValues: generatedWorkloadsApi.workloadReportAPIGetWorkloadLabelValues,
    getWorkloadDataTransferCost: generatedWorkloadsApi.workloadReportAPIGetSingleWorkloadDataTransferCost,
    getRightsizingPatchScript: generatedWorkloadsApi.workloadReportAPIGetClusterWorkloadRightsizingPatch,
    getDataTransferCost: generatedWorkloadsApi.workloadReportAPIGetWorkloadDataTransferCost2,
    getClusterDataTransferSummary: generatedWorkloadsApi.workloadReportAPIGetWorkloadDataTransferCostDailySummary2,
    getWorkloadEfficiencyDetails: generatedWorkloadsApi.workloadReportAPIGetClusterWorkloadEfficiencyReportByName,
    getWorkloadEfficiency: generatedWorkloadsApi.workloadReportAPIGetClusterWorkloadEfficiencyReport2,
    getWorkloadsCostReport: generatedWorkloadsApi.workloadReportAPIGetClusterWorkloadReport2,
    getWorkloadsCostSummaries: generatedWorkloadsApi.workloadReportAPIGetWorkloadCostSummaries,
    getWorkloadTrafficDestinations: generatedWorkloadsApi.workloadReportAPIGetWorkloadTrafficDestinations,
    getWorkloadTrafficDestinationsHistory: generatedWorkloadsApi.workloadReportAPIGetWorkloadTrafficDestinationsHistory,
    getWorkloadsGpuUtilization: generatedWorkloadsApi.workloadReportAPIGetWorkloadsGPUUtilization,
    getWorkloadsGpuUtilizationSummary: generatedWorkloadsApi.workloadReportAPIGetWorkloadsGPUSummary,
    getWorkloadsWastedGPUCostImpact: generatedWorkloadsApi.workloadReportAPIGetWorkloadsWastedGPUCostImpact,
    getWorkloadGpuUtilizationSummary: generatedWorkloadsApi.workloadReportAPIGetSingleWorkloadGPUSummary,
    getWorkloadsMetadata: generatedWorkloadsApi.workloadReportAPIGetWorkloadsMetadata,
    getClusterWorkloadEventMetrics: generatedWorkloadsApi.workloadReportAPIGetClusterWorkloadEventMetrics,
    getOrganizationWorkloadEventMetrics: generatedWorkloadsApi.workloadReportAPIGetOrganizationWorkloadEventMetrics,
    getSingleWorkloadEvents: generatedWorkloadsApi.workloadReportAPIGetSingleWorkloadEvents,

    getNamespace: generatedNamespaceApi.namespaceReportAPIGetSingleNamespaceCostReport,
    getNamespacesCostSummary: generatedNamespaceApi.namespaceReportAPIGetClusterNamespaceCostReportSummaries,
    getNamespacesTotalCosts: generatedNamespaceApi.namespaceReportAPIGetClusterNamespaceCostReport,
    getNamespacesDataTransferCostSummary: generatedNamespaceApi.namespaceReportAPIGetClusterNamespaceDataTransferCost,
    getNamespacesDataTransferTotalCosts: generatedNamespaceApi.namespaceReportAPIGetClusterNamespaceDataTransferTimedTotalCost,


    getOrganizationOverview: organizationOverviewApi.organizationOverviewAPIGetOrganizationOverview,
    getClusterOverview: generatedClusterReportApi.clusterReportAPIGetClusterOverview,
    getOrganizationClustersReport: generatedClusterReportApi.clusterReportAPIGetOrganizationClustersCostReport,
    getOrganizationClustersSummary: generatedClusterReportApi.clusterReportAPIGetClustersSummary,
    getOrganizationClustersDailyCosts: generatedClusterReportApi.clusterReportAPIGetClustersCostReport,
    getOrganizationClustersEfficiencySummary: generatedClusterReportApi.clusterReportAPIGetOrganizationEfficiencySummary,
    getOrganizationClustersTimedEfficiency: generatedClusterReportApi.clusterReportAPIGetOrganizationEfficiencyReport,
    getOrganizationClustersEfficiencyByCluster: generatedClusterReportApi.clusterReportAPIGetOrganizationClustersEfficiencySummary,

    getEstimatedSavings: generatedClusterReportApi.clusterReportAPIGetSavingsRecommendation,
    getRightsizingSummary: generatedClusterReportApi.clusterReportAPIGetRightsizingSummary,
    getCostHistory: generatedClusterReportApi.clusterReportAPIGetClusterCostHistory,
    getCostReport: generatedClusterReportApi.clusterReportAPIGetClusterCostReport,
    getClusterSummary: generatedClusterReportApi.clusterReportAPIGetClusterSummary,
    getClusterEfficiencyDetails: generatedClusterReportApi.clusterReportAPIGetClusterEfficiencyReport,
    getResourceUsage: generatedClusterReportApi.clusterReportAPIGetClusterResourceUsage,
    getUnscheduledPods: generatedClusterReportApi.clusterReportAPIGetClusterUnscheduledPods,
    getSavingsProgress: generatedClusterReportApi.clusterReportAPIGetClusterSavingsReport,
    getClusterAnomalies: generatedClusterReportApi.clusterReportAPIGetCostAnomalies,

    getReportCapabilities: generatedReportConfigurationApi.reportConfigurationAPIGetClusterReportingCapabilities,
    getGpuExporterScript: generatedReportConfigurationApi.reportConfigurationAPIGetGpuMetricsExporterScript,
    getReliabilityMetricsScript:
      generatedReportConfigurationApi.reportConfigurationAPIGetReliabilityMetricsScript,

    getReportingMonetizationStatus:
      generatedReportConfigurationApi.reportConfigurationAPIGetReportingMonetizationStatus,
    markOrgEligibleForReportingMonetization:
      generatedReportConfigurationApi.reportConfigurationAPIMarkOrgEligibleForReportingMonetization,
    startReportingMonetizationTrial:
      generatedReportConfigurationApi.reportConfigurationAPIStartReportingMonetizationTrial,
    createReportingMonetizationCheckoutSession:
      generatedReportConfigurationApi.reportConfigurationAPICreateReportingMonetizationCheckoutSession,
    confirmReportingMonetizationCheckoutSession:
      generatedReportConfigurationApi.reportConfigurationAPIConfirmReportingMonetizationCheckoutSession,
    changeReportingMonetizationSubscriptionPlan:
      generatedReportConfigurationApi.reportConfigurationAPIChangeReportingMonetizationSubscriptionPlan,
    getReportingMonetizationBillingPortal:
      generatedReportConfigurationApi.reportConfigurationAPIGetReportingMonetizationBillingPortal,
    getReportingMonetizationPrices:
      generatedReportConfigurationApi.reportConfigurationAPIGetReportingMonetizationPrices,
    getReportingMonetizationReadiness:
      generatedReportConfigurationApi.reportConfigurationAPIGetReportingMonetizationReadiness,

    getCostComparisonReport: platformImpactReportApi.platformImpactReportAPIGetCostComparisonReport,

    getClusterScoreHistoryV2: generatedClusterReportApi.clusterReportAPIGetClusterScoreHistoryV2,
    getClusterScoreV2: generatedClusterReportApi.clusterReportAPIGetClusterScoreV2,
    regenerateClusterScore: generatedClusterReportApi.clusterReportAPIRecalculateClusterScore,

    getClusterNodeTemplatesReport: nodeReportApi.nodeReportAPIGetClusterNodeTemplatesReport,
    getClusterNodesStorageReport: nodeReportApi.nodeReportAPIGetClusterNodesStorageReport,
    getNodeLabelNames: nodeReportApi.nodeReportAPIGetNodeLabelNames,
    getNodeLabelValues: nodeReportApi.nodeReportAPIGetNodeLabelValues,
    getNodeTemplatesMetrics:
      nodeTemplateApi.nodeTemplateAPIListNodeTemplatesMetricsFiltered,
    getSpotInterruptionPredictionPerformanceMetrics:
      nodeTemplateApi.nodeTemplateAPIListSpotInterruptionPredictionPerformanceMetrics,
    getSpotInterruptionPredictionPerformanceSummary:
      nodeTemplateApi.nodeTemplateAPIGetSpotInterruptionPredictionPerformanceSummary,

    getValueReportTimeline: valueReportApi.valueRealizationAPIRunValueRealizationTimelineReport,
    getValueReportClusterList: valueReportApi.valueRealizationAPIRunClustersValueRealizationReport,
    getValueReportBaselineParams: valueReportApi.valueRealizationAPIGetBaselineParams,
    getValueReportWorkloadsList: valueReportApi.valueRealizationAPIRunWorkloadsValueRealizationReport,
    getClusterAdoptionEvents: valueReportApi.valueRealizationAPIListClusterAdoptionEvents,

    listBudgets: budgetsApi.budgetsAPIListBudgets,
    previewBudgetCost: budgetsApi.budgetsAPIPreviewBudgetCost,
    getBudget: budgetsApi.budgetsAPIGetBudget,
    createBudget: budgetsApi.budgetsAPICreateBudget,
    updateBudget: budgetsApi.budgetsAPIUpdateBudget,
    deleteBudget: budgetsApi.budgetsAPIDeleteBudget,

    listClusterRestrictions: clusterScoreApi.clusterScoreAPIListClusterRestrictions,
    getClusterRestrictionDetails: clusterScoreApi.clusterScoreAPIGetClusterRestrictionDetails,

    listRestrictionSilences:
      clusterScoreApi.clusterScoreAPIListRestrictionSilences,
    createRestrictionSilence:
      clusterScoreApi.clusterScoreAPICreateRestrictionSilence,
    deleteRestrictionSilence:
      clusterScoreApi.clusterScoreAPIDeleteRestrictionSilence,
    previewRestrictionSilence:
      clusterScoreApi.clusterScoreAPIPreviewRestrictionSilence,

    getContainerLiveMigrations:
      containerLiveMigrationApi.containerLiveMigrationAPIListContainerLiveMigrations,
    getContainerLiveMigrationSummary:
      containerLiveMigrationApi.containerLiveMigrationAPIGetContainerLiveMigrationSummary,
    getContainerLiveMigrationDetail:
      containerLiveMigrationApi.containerLiveMigrationAPIGetContainerLiveMigrationDetail,
    listContainerLiveMigrationWorkloads:
      containerLiveMigrationApi.containerLiveMigrationAPIListContainerLiveMigrationWorkloads,
    listContainerLiveMigrationEvents:
      containerLiveMigrationApi.containerLiveMigrationAPIListContainerLiveMigrationEvents,
    listContainerLiveMigrationFilterValues:
      containerLiveMigrationApi.containerLiveMigrationAPIListContainerLiveMigrationFilterValues,
    listContainerLiveMigrationIncompatibleWorkloads:
      containerLiveMigrationApi.containerLiveMigrationAPIListContainerLiveMigrationIncompatibleWorkloads,

    getClusterReliabilityMetrics:
      reliabilityMetricsApi.reliabilityMetricsAPIGetClusterReliabilityMetrics,

    queryCustomDashboards: (
      organizationId: string,
      query: CubeQuery,
      clusterIds?: string[]
    ) => {
      const params = new URLSearchParams();
      clusterIds?.forEach((clusterId) =>
        params.append('clusterIds', clusterId)
      );
      return client.post<CustomDashboardsQueryResponse>(
        `/reporting/v1beta/organizations/${organizationId}/custom-dashboards/query`,
        query,
        { params: clusterIds?.length ? params : undefined }
      );
    },
    getCustomDashboardsSchema: (organizationId: string, clusterIds?: string[]) => {
      const params = new URLSearchParams();
      clusterIds?.forEach((clusterId) =>
        params.append('clusterIds', clusterId)
      );
      return client.get<CustomDashboardsSchemaResponse>(
        `/reporting/v1beta/organizations/${organizationId}/custom-dashboards/schema`,
        { params: clusterIds?.length ? params : undefined }
      );
    },
  };
};
